import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
// Icon set is pinned by yarn.lock to lucide-react 0.400.0 — only import icons
// that exist there (MessageSquareLock, for one, does not).
import { ArrowLeft, Mail, Smartphone } from 'lucide-react';

import { Button } from '../../../v2/components/Button';
import { Input } from '../../../v2/components/Input';

const RESEND_COOLDOWN_S = 30;

const schema = z.object({
    code: z.string().regex(/^\d{6}$/, 'Codul are exact 6 cifre'),
});

export default function TwoFactorForm({ channel, onVerify, onResend, onCancel }) {
    const [cooldown, setCooldown] = useState(RESEND_COOLDOWN_S);
    const {
        register,
        handleSubmit,
        formState: { errors, isSubmitting },
    } = useForm({
        resolver: zodResolver(schema),
        defaultValues: { code: '' },
    });

    useEffect(() => {
        if (cooldown <= 0) return undefined;
        const t = setTimeout(() => setCooldown((s) => s - 1), 1000);
        return () => clearTimeout(t);
    }, [cooldown]);

    const handleResend = async () => {
        await onResend();
        setCooldown(RESEND_COOLDOWN_S);
    };

    return (
        <form
            onSubmit={handleSubmit(({ code }) => onVerify(code))}
            className="flex flex-col gap-5"
            noValidate
        >
            <div>
                <label
                    htmlFor="twofactor-code"
                    className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-200"
                >
                    {channel === 'sms' ? 'Codul din SMS' : 'Codul din email'}
                </label>
                <Input
                    id="twofactor-code"
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    autoFocus
                    maxLength={6}
                    placeholder="000000"
                    icon={channel === 'sms' ? Smartphone : Mail}
                    aria-invalid={!!errors.code}
                    className="h-12 text-center text-lg tracking-[0.5em] sm:h-10"
                    {...register('code')}
                />
                {errors.code && (
                    <p className="mt-1.5 text-xs text-red-600 dark:text-red-400">
                        {errors.code.message}
                    </p>
                )}
            </div>

            <Button
                type="submit"
                size="lg"
                disabled={isSubmitting}
                className="w-full sm:h-10 sm:text-sm"
            >
                {isSubmitting ? 'Se verifica...' : 'Verifica codul'}
            </Button>

            <div className="flex items-center justify-between">
                <button
                    type="button"
                    onClick={onCancel}
                    className="inline-flex items-center gap-1.5 text-sm text-gray-500 underline-offset-4 hover:text-gray-700 hover:underline dark:text-gray-400 dark:hover:text-gray-200"
                >
                    <ArrowLeft aria-hidden="true" className="h-4 w-4 shrink-0" />
                    Inapoi
                </button>
                <button
                    type="button"
                    onClick={handleResend}
                    disabled={cooldown > 0}
                    className="text-sm text-gray-500 underline-offset-4 hover:text-gray-700 hover:underline disabled:cursor-not-allowed disabled:opacity-50 disabled:no-underline dark:text-gray-400 dark:hover:text-gray-200"
                >
                    {cooldown > 0 ? `Retrimite codul (${cooldown}s)` : 'Retrimite codul'}
                </button>
            </div>
        </form>
    );
}
